import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DeleteIcon from "@material-ui/icons/Delete";
import ImageIcon from "@material-ui/icons/Image";
import VideocamIcon from "@material-ui/icons/Videocam";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import EventIcon from "@material-ui/icons/Event";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import ImagesStoreService from "./Locality/Service/imagesStoreService";

const MAX_IMAGES = 10;
const MAX_VIDEOS = 5;
const MAX_VIDEO_SIZE_MB = 200;
const MAX_VIDEO_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;

const useStyles = makeStyles((theme) => ({
  page: {
    maxWidth: 1040,
    margin: "0 auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
    fontSize: "1.6rem",
    color: "#0f2d4d",
  },
  detailsCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
  },
  detailRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  panel: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    padding: theme.spacing(2.5),
    position: "relative",
  },
  tabs: {
    borderBottom: "1px solid #e6eaf0",
    marginBottom: theme.spacing(2),
  },
  uploadActions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  uploadIconBtn: {
    border: "1px dashed #1769d6",
    borderRadius: 10,
    width: 72,
    height: 72,
    color: "#1769d6",
    backgroundColor: "#eef4ff",
    "&:hover": { backgroundColor: "#e0ebff" },
  },
  sectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#46566b",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  previewGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
  },
  previewItem: {
    position: "relative",
    width: 100,
  },
  thumb: {
    width: 100,
    height: 100,
    objectFit: "cover",
    borderRadius: 8,
    border: "1px solid #e6eaf0",
    display: "block",
  },
  videoTile: {
    width: 100,
    height: 100,
    borderRadius: 8,
    border: "1px solid #e6eaf0",
    backgroundColor: "#0f2d4d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  fileName: {
    fontSize: "0.65rem",
    maxWidth: 100,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "center",
    marginTop: 2,
  },
  removeBtn: {
    position: "absolute",
    top: 2,
    right: 2,
    backgroundColor: "rgba(255,255,255,0.95)",
    padding: 4,
  },
  primaryBtn: {
    backgroundColor: "#1769d6",
    color: "#fff",
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    "&:hover": { backgroundColor: "#125bbd" },
  },
  overlay: {
    position: "absolute",
    inset: 0,
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1.5),
    backgroundColor: "rgba(255,255,255,0.94)",
    borderRadius: 14,
    padding: theme.spacing(3),
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
}));

const getStoredUserDetails = () => {
  try {
    return JSON.parse(localStorage.getItem("userDetail") || "{}");
  } catch {
    return {};
  }
};

const normalizeId = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "object" && value._id) return String(value._id);
  return String(value);
};

const resolveName = (value, key) => {
  if (!value) return "";
  if (typeof value === "object") return value[key] || "";
  return "";
};

const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

const mergeSelectedFiles = (existing, incoming) => {
  const keys = new Set(existing.map(fileKey));
  return [...existing, ...incoming.filter((f) => !keys.has(fileKey(f)))];
};

export default function ImagesStoreAddMedia() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ percent: 0, message: "" });
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null);
  const [mediaTab, setMediaTab] = useState("images");

  const [imageUrls, setImageUrls] = useState([]);
  const [videoUrls, setVideoUrls] = useState([]);
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingVideos, setPendingVideos] = useState([]);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const userDetails = useMemo(() => getStoredUserDetails(), []);
  const role = userDetails.role || "";

  const loadData = useCallback(async () => {
    if (!id) {
      setError("Record not found.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await ImagesStoreService.getImagesStoreById(id);
      const data = res?.data || res;
      if (!data?._id) {
        setError("Record not found.");
        setLoading(false);
        return;
      }
      setRecord(data);
      setImageUrls(Array.isArray(data.imageURL) ? data.imageURL.filter(Boolean) : []);
      setVideoUrls(Array.isArray(data.videoURL) ? data.videoURL.filter(Boolean) : []);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err?.message || "Failed to load record.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const pendingImagePreviews = useMemo(
    () =>
      pendingImages.map((file) => ({
        file,
        key: fileKey(file),
        url: URL.createObjectURL(file),
      })),
    [pendingImages],
  );

  useEffect(
    () => () => {
      pendingImagePreviews.forEach((p) => URL.revokeObjectURL(p.url));
    },
    [pendingImagePreviews],
  );

  const schooleId = normalizeId(record?.schooleId) || normalizeId(userDetails?.schooleId);

  const handleImageFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setPendingImages((prev) => {
      const merged = mergeSelectedFiles(prev, picked);
      if (merged.length > MAX_IMAGES) {
        alert(`You can upload at most ${MAX_IMAGES} images at a time.`);
        return prev;
      }
      return merged;
    });
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleVideoFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const oversized = picked.filter((f) => f.size > MAX_VIDEO_BYTES);
    if (oversized.length) {
      alert(`Each video must be ${MAX_VIDEO_SIZE_MB} MB or smaller.`);
      if (videoInputRef.current) videoInputRef.current.value = "";
      return;
    }
    setPendingVideos((prev) => {
      const merged = mergeSelectedFiles(prev, picked);
      if (merged.length > MAX_VIDEOS) {
        alert(`You can upload at most ${MAX_VIDEOS} videos at a time.`);
        return prev;
      }
      return merged;
    });
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const handleUpload = async (type) => {
    const isVideo = type === "videos";
    const pending = isVideo ? pendingVideos : pendingImages;
    const fieldName = isVideo ? "videos" : "images";

    if (!pending.length) {
      alert(`Please select ${isVideo ? "videos" : "images"} first.`);
      return;
    }
    if (!schooleId) {
      alert("School is required for upload.");
      return;
    }

    const formData = new FormData();
    pending.forEach((file) => formData.append(fieldName, file));
    if (role === "SUPERADMIN") {
      formData.append("schooleId", schooleId);
    }

    setUploading(true);
    setUploadProgress({ percent: 0, message: "Sending files to server…" });
    try {
      const result = await ImagesStoreService.uploadMedia(formData, (prog) => {
        setUploadProgress({
          percent: prog.percent || 0,
          message: `Sending to server: ${prog.percent || 0}%`,
        });
      });
      const newUrls = isVideo ? (result.videoUrls || []) : (result.imageUrls || []);
      if (!newUrls.length) {
        throw new Error(result.message || "Upload did not return any URLs.");
      }
      if (isVideo) {
        setVideoUrls((prev) => [...prev, ...newUrls]);
        setPendingVideos([]);
        if (videoInputRef.current) videoInputRef.current.value = "";
      } else {
        setImageUrls((prev) => [...prev, ...newUrls]);
        setPendingImages([]);
        if (imageInputRef.current) imageInputRef.current.value = "";
      }
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Upload failed");
    } finally {
      setUploading(false);
      setUploadProgress({ percent: 0, message: "" });
    }
  };

  const removeUploadedImage = (index) => {
    const url = imageUrls[index];
    setImageUrls((prev) => prev.filter((_, i) => i !== index));
    if (url && !url.startsWith("data:")) {
      ImagesStoreService.deleteAzureImage({ imageURL: url }).catch(() => {});
    }
  };

  const removeUploadedVideo = (index) => {
    const url = videoUrls[index];
    setVideoUrls((prev) => prev.filter((_, i) => i !== index));
    if (url && !url.startsWith("data:")) {
      ImagesStoreService.deleteAzureImage({ imageURL: url }).catch(() => {});
    }
  };

  const handleUpdate = async () => {
    if (!record?._id) return;
    setSaving(true);
    try {
      await ImagesStoreService.updateImagesStore(record._id, {
        imageURL: imageUrls.filter(Boolean),
        videoURL: videoUrls.filter(Boolean),
        schooleId: schooleId || undefined,
      });
      alert("Media updated successfully!");
      history.push("/app/imagesstore");
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Failed to update media");
    } finally {
      setSaving(false);
    }
  };

  const renderTabPanel = (type) => {
    const isVideo = type === "videos";
    const pending = isVideo ? pendingVideos : pendingImages;
    const uploaded = isVideo ? videoUrls : imageUrls;
    const inputId = isVideo ? "add-media-video-input" : "add-media-image-input";
    const inputRef = isVideo ? videoInputRef : imageInputRef;
    const onFileChange = isVideo ? handleVideoFileChange : handleImageFileChange;
    const accept = isVideo ? "video/*,.mp4,.webm,.mov,.m4v" : "image/*";
    const maxCount = isVideo ? MAX_VIDEOS : MAX_IMAGES;
    const onRemovePending = isVideo
      ? (idx) => setPendingVideos((prev) => prev.filter((_, i) => i !== idx))
      : (idx) => setPendingImages((prev) => prev.filter((_, i) => i !== idx));
    const onRemoveUploaded = isVideo ? removeUploadedVideo : removeUploadedImage;
    const UploadIcon = isVideo ? VideocamIcon : ImageIcon;

    const pendingList = isVideo
      ? pending.map((file, idx) => ({ file, idx, previewUrl: "" }))
      : pendingImagePreviews.map((p, idx) => ({ file: p.file, idx, previewUrl: p.url }));

    return (
      <Box>
        <input
          ref={inputRef}
          accept={accept}
          style={{ display: "none" }}
          id={inputId}
          type="file"
          multiple
          onChange={onFileChange}
        />
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          {isVideo
            ? `Select up to ${MAX_VIDEOS} videos (max ${MAX_VIDEO_SIZE_MB} MB each). Stored without compression.`
            : `Select up to ${MAX_IMAGES} images. Stored without compression.`}
        </Typography>

        <Box className={classes.uploadActions}>
          <label htmlFor={inputId}>
            <IconButton
              component="span"
              className={classes.uploadIconBtn}
              disabled={uploading || !schooleId}
              aria-label={isVideo ? "Select videos" : "Select images"}
            >
              <UploadIcon style={{ fontSize: 32 }} />
            </IconButton>
          </label>
          {pending.length > 0 && (
            <>
              <Button
                variant="contained"
                className={classes.primaryBtn}
                onClick={() => handleUpload(type)}
                disabled={uploading || !schooleId}
                size="small"
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {uploading ? "Uploading…" : `Upload ${pending.length}`}
              </Button>
              <Button
                variant="text"
                size="small"
                disabled={uploading}
                onClick={() => (isVideo ? setPendingVideos([]) : setPendingImages([]))}
              >
                Clear selection
              </Button>
            </>
          )}
        </Box>

        {pending.length > 0 && (
          <Box style={{ marginBottom: 8 }}>
            <Typography className={classes.sectionLabel}>
              Ready to upload ({pending.length}/{maxCount})
            </Typography>
            <Box className={classes.previewGrid}>
              {pendingList.map(({ file, idx, previewUrl }) => (
                <Box key={fileKey(file)} className={classes.previewItem}>
                  {isVideo ? (
                    <Box className={classes.videoTile} title={file.name}>
                      <VideocamIcon style={{ color: "#fff", fontSize: 32 }} />
                    </Box>
                  ) : (
                    <img src={previewUrl} alt={file.name} className={classes.thumb} />
                  )}
                  <Typography className={classes.fileName} title={file.name}>
                    {file.name}
                  </Typography>
                  <IconButton
                    size="small"
                    className={classes.removeBtn}
                    onClick={() => onRemovePending(idx)}
                    disabled={uploading}
                    aria-label="Remove file"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Typography className={classes.sectionLabel}>
          Uploaded {isVideo ? "videos" : "images"} ({uploaded.length})
        </Typography>
        {uploaded.length > 0 ? (
          <Box className={classes.previewGrid}>
            {uploaded.map((url, idx) => (
              <Box key={`${url}-${idx}`} className={classes.previewItem}>
                {isVideo ? (
                  <Box
                    className={classes.videoTile}
                    title={url}
                    onClick={() => window.open(url, "_blank", "noopener")}
                  >
                    <PlayArrowIcon style={{ color: "#fff", fontSize: 32 }} />
                  </Box>
                ) : (
                  <img src={url} alt="" className={classes.thumb} />
                )}
                <IconButton
                  size="small"
                  className={classes.removeBtn}
                  onClick={() => onRemoveUploaded(idx)}
                  aria-label="Remove uploaded file"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No {isVideo ? "videos" : "images"} uploaded yet.
          </Typography>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" padding={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.page}>
        <Box className={classes.header}>
          <IconButton onClick={() => history.push("/app/imagesstore")} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Typography className={classes.title}>Add Media</Typography>
        </Box>
        <Card elevation={0} className={classes.detailsCard}>
          <Typography color="error">{error}</Typography>
        </Card>
      </Box>
    );
  }

  const className = resolveName(record?.classId, "className");
  const sectionName = resolveName(record?.sectionId, "sectionName");

  return (
    <Box className={classes.page}>
      <Box className={classes.header}>
        <IconButton onClick={() => history.push("/app/imagesstore")} aria-label="Back">
          <ArrowBackIcon />
        </IconButton>
        <Typography className={classes.title}>Add Media</Typography>
      </Box>

      <Card elevation={0} className={classes.detailsCard}>
        <Typography style={{ fontWeight: 700, fontSize: "1.15rem", color: "#0f2d4d" }}>
          {record?.eventName || "—"}
        </Typography>
        <Box className={classes.detailRow}>
          {className ? <Chip size="small" label={`Class: ${className}`} /> : null}
          {sectionName ? <Chip size="small" label={`Section: ${sectionName}`} /> : null}
          {record?.date ? (
            <Chip
              size="small"
              icon={<EventIcon style={{ fontSize: 16 }} />}
              label={record.date}
            />
          ) : null}
          <Chip
            size="small"
            label={record?.status === "published" ? "Published" : "Draft"}
            style={{
              backgroundColor: record?.status === "published" ? "#e6f7ed" : "#eef4ff",
              color: record?.status === "published" ? "#1a9b54" : "#1769d6",
            }}
          />
        </Box>
        <Box className={classes.detailRow}>
          <Typography variant="body2" color="textSecondary">
            {imageUrls.length} image(s) · {videoUrls.length} video(s)
          </Typography>
        </Box>
      </Card>

      <Card elevation={0} className={classes.panel}>
        <Tabs
          value={mediaTab}
          onChange={(_, value) => setMediaTab(value)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabs}
        >
          <Tab icon={<ImageIcon />} label={`Images (${imageUrls.length})`} value="images" />
          <Tab icon={<VideocamIcon />} label={`Videos (${videoUrls.length})`} value="videos" />
        </Tabs>

        {mediaTab === "images" ? renderTabPanel("images") : renderTabPanel("videos")}

        {uploading && (
          <Box className={classes.overlay}>
            <CircularProgress size={48} thickness={4} style={{ color: "#1769d6" }} />
            <Typography style={{ fontWeight: 700, color: "#0f2d4d" }}>
              Uploading {mediaTab === "videos" ? "videos" : "images"}…
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              {uploadProgress.message || "Please wait…"}
            </Typography>
            <Box style={{ width: "100%", maxWidth: 280 }}>
              <LinearProgress
                variant={uploadProgress.percent > 0 ? "determinate" : "indeterminate"}
                value={uploadProgress.percent}
                style={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Box>
        )}

        <Divider style={{ marginTop: 20 }} />
        <Box className={classes.footer}>
          <Button onClick={() => history.push("/app/imagesstore")} disabled={saving || uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className={classes.primaryBtn}
            onClick={handleUpdate}
            disabled={saving || uploading}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Updating…" : "Update"}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
